import dayjs from 'dayjs'
import type { UseFormSetValue } from 'react-hook-form'
import { formatHmm, parseHmm } from '@/lib/flight-log-utils'
import type { FolioExtraction, FolioLeg } from '@@api/flightFolioScan.shared'
import type { FlightLogFormData } from './form'

// Maps a Claude-extracted paper folio onto the flight log form. Every folio
// cell is a raw string exactly as written on the sheet ('' = blank), so each
// value is normalized before it's applied; unparseable or blank cells are
// skipped and existing form values stand.
//
// Confidence gate: cells the extractor flagged in `lowConfidenceFields`
// (reading < 90% certain) are never auto-filled — they stay visible in the
// scan result for the pilot to copy by hand. Returns how many fields were set.

const txt = (s: string | undefined): string | null => {
  const t = (s ?? '').trim()
  return t.length ? t : null
}

const num = (s: string | undefined): number | null => {
  const t = (s ?? '').replace(/[^0-9.-]/g, '')
  if (!t) return null
  const n = parseFloat(t)
  return Number.isFinite(n) ? n : null
}

const int = (s: string | undefined): number | null => {
  const t = (s ?? '').replace(/\D/g, '')
  if (!t) return null
  const n = parseInt(t, 10)
  return Number.isFinite(n) ? n : null
}

// "13:45", "1345", "13.45" → "13:45"
const clock = (s: string | undefined): string | null => {
  const m = (s ?? '').trim().match(/^(\d{1,2})[:.]?(\d{2})$/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const mm = parseInt(m[2], 10)
  if (h > 23 || mm > 59) return null
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

// Unbounded "h:mm" duration/total ("2419:58", "13" → "13:00")
const hmm = (s: string | undefined): string | null => {
  const minutes = parseHmm(txt(s))
  return minutes === null ? null : formatHmm(minutes)
}

const isoDate = (s: string | undefined): string | null => {
  const t = txt(s)
  if (!t) return null
  const parsed = dayjs(t)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

// "legs[0].offBlockUtc" / " legs.0.offBlockUtc " → "legs.0.offBlockUtc"
const normPath = (p: string) =>
  p
    .trim()
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/\.{2,}/g, '.')
    .replace(/^\.|\.$/g, '')

// Registrations compare with spaces/dashes stripped ("N-103 SW" ≡ "N103SW")
const normReg = (s: string) => s.toUpperCase().replace(/[\s-]/g, '')

export interface FolioMatchResult {
  // Non-null when the sheet confidently names a different aircraft
  registrationMismatch: { scanned: string; expected: string } | null
  // True when a leg row matches this log's route (or the sheet has one row)
  routeMatch: boolean
}

// Sanity check before applying: does the photographed sheet belong to this
// flight? Registration only counts when it was read confidently.
export function checkFolioMatch(
  extracted: FolioExtraction,
  expected: { from: string; to: string; registration: string | null | undefined }
): FolioMatchResult {
  const lowConfidence = new Set(
    ((extracted.lowConfidenceFields as string[] | undefined) ?? []).map(normPath)
  )

  let registrationMismatch: FolioMatchResult['registrationMismatch'] = null
  const scannedReg = (extracted.header.registrationNo ?? '').trim()
  if (
    scannedReg &&
    expected.registration &&
    !lowConfidence.has('header.registrationNo') &&
    normReg(scannedReg) !== normReg(expected.registration)
  ) {
    registrationMismatch = { scanned: scannedReg, expected: expected.registration }
  }

  const from = expected.from.trim().toUpperCase()
  const to = expected.to.trim().toUpperCase()
  const routeMatch =
    extracted.legs.length === 1 ||
    extracted.legs.some(
      (l) => l.from.trim().toUpperCase() === from && l.to.trim().toUpperCase() === to
    )

  return { registrationMismatch, routeMatch }
}

export function applyFolioExtraction(
  extracted: FolioExtraction,
  values: FlightLogFormData,
  setValue: UseFormSetValue<FlightLogFormData>
): number {
  let applied = 0
  const set = <P extends Parameters<UseFormSetValue<FlightLogFormData>>[0]>(
    path: P,
    value: unknown
  ) => {
    if (value === null || value === undefined) return
    setValue(path, value as never, { shouldDirty: true })
    applied += 1
  }

  // Old stored scans predate lowConfidenceFields — treat as all-confident.
  const lowConfidence = new Set(
    ((extracted.lowConfidenceFields as string[] | undefined) ?? []).map(normPath)
  )
  // Returns the raw cell unless the extractor flagged its reading as uncertain
  const take = (path: string, raw: string | undefined): string | undefined =>
    lowConfidence.has(path) ? undefined : raw

  // The sheet holds up to 4 leg rows — apply only the row matching this
  // log's route (or the sole row); a wrong-leg autofill is worse than none.
  const from = values.routeTimes.from.trim().toUpperCase()
  const to = values.routeTimes.to.trim().toUpperCase()
  const leg: FolioLeg | null =
    extracted.legs.find(
      (l) => l.from.trim().toUpperCase() === from && l.to.trim().toUpperCase() === to
    ) ??
    (extracted.legs.length === 1 ? extracted.legs[0] : null)

  if (leg) {
    const li = extracted.legs.indexOf(leg)
    const cell = (name: keyof FolioLeg) => take(`legs.${li}.${name}`, leg[name])

    set('routeTimes.offBlock', clock(cell('offBlockUtc')))
    set('routeTimes.takeOff', clock(cell('takeOffUtc')))
    set('routeTimes.landing', clock(cell('landingUtc')))
    set('routeTimes.onBlock', clock(cell('onBlockUtc')))
    set('routeTimes.distanceNm', num(cell('distNm')))

    // Hours matrix: the leg row carries this-leg values
    set('hours.airframe.thisLeg', hmm(cell('airframeHours')))
    set('hours.apu.thisLeg', hmm(cell('apuHours')))
    ;(['engineHoursNo1', 'engineHoursNo2', 'engineHoursNo3'] as const).forEach((name, i) => {
      if (i < values.hours.engines.length) set(`hours.engines.${i}.thisLeg`, hmm(cell(name)))
    })
    ;(['engineCyclesNo1', 'engineCyclesNo2', 'engineCyclesNo3'] as const).forEach((name, i) => {
      if (i < values.hours.cycles.engines.length)
        set(`hours.cycles.engines.${i}.thisLeg`, int(cell(name)))
    })
    set('hours.cycles.landings.thisLeg', int(cell('totalLandings')))
  }

  // Brought-forward row (only meaningful when the cells parse)
  const bf = extracted.totalsBroughtForward
  const bfCell = (name: keyof typeof bf) => take(`totalsBroughtForward.${name}`, bf[name])
  set('hours.airframe.broughtFwd', hmm(bfCell('airframeHours')))
  set('hours.apu.broughtFwd', hmm(bfCell('apuHours')))
  ;(['engineHoursNo1', 'engineHoursNo2', 'engineHoursNo3'] as const).forEach((name, i) => {
    if (i < values.hours.engines.length) set(`hours.engines.${i}.broughtFwd`, hmm(bfCell(name)))
  })
  ;(['engineCyclesNo1', 'engineCyclesNo2', 'engineCyclesNo3'] as const).forEach((name, i) => {
    if (i < values.hours.cycles.engines.length)
      set(`hours.cycles.engines.${i}.broughtFwd`, int(bfCell(name)))
  })
  set('hours.cycles.landings.broughtFwd', int(bfCell('totalLandings')))

  // Fuel
  const fuel = extracted.fuel
  const fuelCell = (name: keyof typeof fuel) => take(`fuel.${name}`, fuel[name])
  set('fuel.plannedUpliftL', num(fuelCell('plannedUpliftLts')))
  set('fuel.actualUpliftL', num(fuelCell('actualUpliftLts')))
  set('fuel.fuelDocNo', txt(fuelCell('fuelDocNo')))
  set('fuel.departTotalLbs', num(fuelCell('departTotalLbs')))
  set('fuel.arrivalTotalLbs', num(fuelCell('arrivalTotalLbs')))

  // De-ice: any legible, confident cell means the section applies
  const deice = extracted.deice
  const deiceCell = (name: keyof typeof deice) => take(`deice.${name}`, deice[name])
  const deiceStart = clock(deiceCell('start'))
  const deiceStop = clock(deiceCell('stop'))
  const deiceType = txt(deiceCell('type'))
  const deiceRatio = txt(deiceCell('ratioMix'))
  if (deiceStart || deiceStop || deiceType || deiceRatio) {
    set('deice.performed', true)
    set('deice.startTime', deiceStart)
    set('deice.stopTime', deiceStop)
    set('deice.fluidType', deiceType)
    set('deice.mixRatio', deiceRatio)
  }

  // Oil uplift (the sheet has no post-shutdown remaining cells)
  const oil = extracted.oil
  const oilCell = (name: keyof typeof oil) => take(`oil.${name}`, oil[name])
  ;(['engineOilUpliftNo1', 'engineOilUpliftNo2', 'engineOilUpliftNo3'] as const).forEach(
    (name, i) => {
      if (i < values.oil.engines.length) set(`oil.engines.${i}.upliftQts`, num(oilCell(name)))
    }
  )
  set('oil.apuUpliftQts', num(oilCell('apuOilUplift')))
  set('oil.hydUpliftQts', num(oilCell('hydOilUplift')))
  set('oil.psiDif', num(oilCell('psiDif')))

  // ECTM: cruise scalars read column 1; per-engine rows map column i → engine i
  const ectm = extracted.ectm
  const ectmCell = (metric: keyof typeof ectm, column: 'value1' | 'value2' | 'value3') =>
    take(`ectm.${metric}.${column}`, ectm[metric][column])
  set('ectm.weightKg', num(ectmCell('weight', 'value1')))
  set('ectm.oatSatC', num(ectmCell('oatSat', 'value1')))
  set('ectm.iasKt', num(ectmCell('ias', 'value1')))
  set('ectm.tasKt', num(ectmCell('tas', 'value1')))
  set('ectm.mach', num(ectmCell('mach', 'value1')))
  set('ectm.flightLevel', int(ectmCell('fl', 'value1')))
  const perEngine = [
    ['fuelFlow', 'fuelFlow'],
    ['n1Pct', 'n1Prop'],
    ['n2Pct', 'n2Ng'],
    ['itt', 'itt'],
    ['oilPress', 'oilPress'],
    ['oilTemp', 'oilTemp'],
    ['fuelTemp', 'fuelTemp'],
    ['bleedPress', 'bleedPress'],
  ] as const
  for (const [formKey, folioKey] of perEngine) {
    ;(['value1', 'value2', 'value3'] as const).forEach((column, i) => {
      if (i < values.ectm.engines.length)
        set(`ectm.engines.${i}.${formKey}`, num(ectmCell(folioKey, column)))
    })
  }

  // Inspections
  set('inspection.dueHours', hmm(take('inspections.dueHourly', extracted.inspections.dueHourly)))
  set(
    'inspection.dueDate',
    isoDate(take('inspections.dueCalendar', extracted.inspections.dueCalendar))
  )

  // Sign-off is never applied from the sheet: the PIC name and licence number
  // always come from the pilot's crew record, and the signature is drawn on
  // the pad. The scanned cells stay visible in the scan result only.

  // Defects: only fill an empty section — appending onto hand-entered rows
  // risks duplicates. A row whose description reading is uncertain is dropped
  // entirely (a wrong defect description is worse than a missing one).
  const defectRows = extracted.defects
    .map((d, i) => ({ d, i }))
    .filter(
      ({ d, i }) =>
        txt(take(`defects.${i}.description`, d.description)) ||
        txt(d.defectYn)?.toUpperCase().startsWith('Y')
    )
  if (defectRows.length > 0 && values.defects.entries.length === 0) {
    set('defects.hasDefects', true)
    set(
      'defects.entries',
      defectRows.map(({ d, i }) => ({
        description: txt(take(`defects.${i}.description`, d.description)) ?? '',
        action: txt(d.actionCode)?.toUpperCase().startsWith('D') ? ('D' as const) : ('A' as const),
        melRef: txt(take(`defects.${i}.melRef`, d.melRef)),
        actionNotes: txt(take(`defects.${i}.actionTaken`, d.actionTaken)),
      }))
    )
  }

  return applied
}
